import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Controller,
    useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    Group as GroupIcon,
    Person as PersonIcon,
    DirectionsCar as DirectionsCarIcon,
    AddCircle as AddCircleIcon,
    RemoveCircle as RemoveCircleIcon,
    Assignment as AssignmentIcon,
} from '@mui/icons-material';

import { useEmployees } from '../../../hooks/useEmployees'; // Asume que existe
import { useVehicles } from '../../../hooks/useVehicles';
import { useServicesTypesProject } from '../../../hooks/administration/useServicesTypesProject';

// ============================================================
// ESQUEMA DE VALIDACIÓN
// ============================================================
const teamSchema = yup.object({
    empId: yup
        .number()
        .transform((value, original) => (original === '' || original === null ? undefined : value))
        .typeError('Debe seleccionar un empleado')
        .integer('Empleado inválido')
        .positive('Empleado inválido')
        .required('El conductor es obligatorio'),

    vehId: yup
        .number()
        .transform((value, original) => (original === '' || original === null ? undefined : value))
        .typeError('Debe seleccionar un vehículo')
        .integer('Vehículo inválido')
        .positive('Vehículo inválido')
        .required('El vehículo es obligatorio'),

    nombre: yup
        .string()
        .trim()
        .max(150, 'Máximo 150 caracteres')
        .nullable(),

    serProIds: yup
        .array()
        .of(yup.number().integer().positive())
        .min(1, 'Debe seleccionar al menos un servicio-proyecto')
        .required('Los servicios son obligatorios'),
});

// ============================================================
// HELPERS
// ============================================================
const getSerProId = (item) => {
    const id = Number(item?.serproid ?? item?.SerProID ?? item?.serProId);
    return Number.isInteger(id) && id > 0 ? id : null;
};

const getProNom = (item) => item?.pronom ?? item?.ProNom ?? '-';
const getSerProNom = (item) => item?.serpronom ?? item?.SerProNom ?? '-';
const getTipSerNom = (item) => item?.tipsernom ?? item?.TipSerNom ?? '-';

// ============================================================
// COMPONENTE
// ============================================================
const TeamForm = ({
    open,
    handleClose,
    onSubmit,
    defaultValues = {},
    isSubmitting = false,
}) => {
    const isEditing = Boolean(defaultValues?.equConVehId);

    // Estados para gestión de servicios
    const [assignedServiceIds, setAssignedServiceIds] = useState([]);
    const [serviceSearch, setServiceSearch] = useState('');

    // Hooks de datos
    const { employees = [], isLoading: loadingEmployees } = useEmployees();
    const { vehicles = [], isLoading: loadingVehicles } = useVehicles();
    const { servicesTypesProject = [], isLoading: loadingServices } = useServicesTypesProject();

    // React Hook Form
    const {
        control,
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isSubmitting: isFormSubmitting },
    } = useForm({
        resolver: yupResolver(teamSchema),
        defaultValues: {
            empId: '',
            vehId: '',
            nombre: '',
            serProIds: [],
        },
    });

    // Servicios disponibles y asignados
    const servicesOptions = useMemo(() => servicesTypesProject, [servicesTypesProject]);

    const assignedServices = useMemo(() => {
        const set = new Set(assignedServiceIds);
        return servicesOptions.filter((s) => set.has(getSerProId(s)));
    }, [servicesOptions, assignedServiceIds]);

    const availableServices = useMemo(() => {
        const set = new Set(assignedServiceIds);
        return servicesOptions.filter((s) => !set.has(getSerProId(s)));
    }, [servicesOptions, assignedServiceIds]);

    const filteredAvailableServices = useMemo(() => {
        const term = serviceSearch.trim().toLowerCase();
        if (!term) return availableServices;
        return availableServices.filter((s) => {
            const fields = [getSerProNom(s), getProNom(s), getTipSerNom(s)];
            return fields.some((f) => String(f).toLowerCase().includes(term));
        });
    }, [availableServices, serviceSearch]);

    // Reset al abrir
    useEffect(() => {
        if (!open) return;

        const initialSerProIds = defaultValues?.serProIds ?? defaultValues?.serviciosProyecto?.map((s) => getSerProId(s)) ?? [];
        reset({
            empId: defaultValues?.empId ?? defaultValues?.EmpID ?? '',
            vehId: defaultValues?.vehId ?? defaultValues?.VehID ?? '',
            nombre: defaultValues?.nombre ?? defaultValues?.equConVehNom ?? '',
            serProIds: initialSerProIds,
        });
        setAssignedServiceIds(initialSerProIds.filter(Boolean));
        setServiceSearch('');
    }, [open, reset, defaultValues]);

    // Sincronizar servicios con React Hook Form
    useEffect(() => {
        setValue('serProIds', assignedServiceIds, {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [assignedServiceIds, setValue]);

    const employeesById = useMemo(
        () =>
            new Map(
                employees.map((emp) => [
                    Number(emp.empid),
                    emp,
                ])
            ),
        [employees]
    );

    const vehiclesById = useMemo(
        () =>
            new Map(
                vehicles.map((veh) => [
                    Number(veh.vehid),
                    veh,
                ])
            ),
        [vehicles]
    );

    // Handlers
    const handleFormClose = useCallback(() => {
        if (isSubmitting || isFormSubmitting) return;
        reset();
        setAssignedServiceIds([]);
        setServiceSearch('');
        handleClose();
    }, [reset, handleClose, isSubmitting, isFormSubmitting]);

    const handleAddService = useCallback((service) => {
        const id = getSerProId(service);
        if (!id) return;
        setAssignedServiceIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }, []);

    const handleRemoveService = useCallback((id) => {
        setAssignedServiceIds((prev) => prev.filter((sid) => sid !== id));
    }, []);

    const handleFormSubmit = useCallback(
        (data) => {
            const payload = {
                empId: Number(data.empId),
                vehId: Number(data.vehId),
                nombre: data.nombre?.trim() || null,
                serProIds: assignedServiceIds,
            };
            onSubmit(payload);
        },
        [onSubmit, assignedServiceIds]
    );

    const submitting = isSubmitting || isFormSubmitting;

    return (
        <Dialog
            open={open}
            onClose={handleFormClose}
            fullWidth
            maxWidth="md"
            disableEscapeKeyDown={submitting}
        >
            <DialogTitle
                sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <GroupIcon />
                {isEditing ? 'Editar equipo' : 'Crear nuevo equipo'}
            </DialogTitle>

            <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        {/* Selección de Empleado (Conductor) */}
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="h6"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <PersonIcon color="primary" />
                                Conductor
                            </Typography>

                            <Controller
                                name="empId"
                                control={control}
                                render={({ field }) => {
                                    const selectedEmployee = employeesById.get(Number(field.value)) ?? null;

                                    return (
                                        <Autocomplete
                                            options={employees}
                                            value={selectedEmployee}
                                            loading={loadingEmployees}
                                            disabled={submitting || loadingEmployees}
                                            autoHighlight
                                            clearOnEscape
                                            getOptionLabel={(emp) =>
                                                `${emp.pernom ?? ''} ${emp.perape ?? ''} - ${emp.nomusu ?? ''}`.trim()
                                            }
                                            isOptionEqualToValue={(option, value) =>
                                                Number(option.empid) === Number(value.empid)
                                            }
                                            filterOptions={(options, state) => {
                                                const term = state.inputValue.trim().toLowerCase();

                                                if (!term) { return options; }

                                                return options.filter(
                                                    (emp) => {
                                                        const searchable =
                                                            [
                                                                emp.pernom,
                                                                emp.perape,
                                                                emp.nomusu,
                                                                emp.empid,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' ')
                                                                .toLowerCase();

                                                        return searchable.includes(term);
                                                    }
                                                );
                                            }}
                                            onChange={(_, value) => {
                                                field.onChange(value ? Number(value.empid) : '');
                                            }}
                                            renderOption={(props, emp) => (
                                                <Box component="li" {...props} key={emp.empid} >
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600} >
                                                            {emp.pernom}{' '}
                                                            {emp.perape}
                                                        </Typography>

                                                        <Typography variant="caption" color="text.secondary" >
                                                            {emp.nomusu}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Buscar conductor"
                                                    placeholder="Nombre, apellido o usuario"
                                                    margin="normal"
                                                    error={Boolean(errors.empId)}
                                                    helperText={errors.empId?.message}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <InputAdornment position="start">
                                                                    <PersonIcon
                                                                        color={errors.empId ? 'error' : 'action'}
                                                                    />
                                                                </InputAdornment>

                                                                {
                                                                    params.InputProps.startAdornment
                                                                }
                                                            </>
                                                        ),
                                                        endAdornment: (
                                                            <>
                                                                {loadingEmployees ? (
                                                                    <CircularProgress size={20} />
                                                                ) : null}

                                                                {
                                                                    params.InputProps.endAdornment
                                                                }
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    );
                                }}
                            />
                        </Grid>

                        {/* Selección de Vehículo */}
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="h6"
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, }}
                            >
                                <DirectionsCarIcon color="primary" />
                                Vehículo
                            </Typography>

                            <Controller
                                name="vehId"
                                control={control}
                                render={({ field }) => {
                                    const selectedVehicle = vehiclesById.get(Number(field.value)) ?? null;

                                    return (
                                        <Autocomplete
                                            options={vehicles}
                                            value={selectedVehicle}
                                            loading={loadingVehicles}
                                            disabled={submitting || loadingVehicles}
                                            autoHighlight
                                            clearOnEscape
                                            getOptionLabel={(veh) =>
                                                `${veh.vehmat ?? ''} - ${veh.vehmar ?? ''} ${veh.vehmod ?? ''}`.trim()
                                            }
                                            isOptionEqualToValue={(option, value) =>
                                                Number(option.vehid) === Number(value.vehid)
                                            }
                                            filterOptions={(options, state) => {
                                                const term = state.inputValue.trim().toLowerCase();

                                                if (!term) { return options; }

                                                return options.filter(
                                                    (veh) => {
                                                        const searchable =
                                                            [
                                                                veh.vehmat,
                                                                veh.vehmar,
                                                                veh.vehmod,
                                                                veh.vehdes,
                                                                veh.vehani,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' ')
                                                                .toLowerCase();

                                                        return searchable.includes(term);
                                                    }
                                                );
                                            }}
                                            onChange={(_, value) => {
                                                field.onChange(value ? Number(value.vehid) : '');
                                            }}
                                            renderOption={(props, veh) => (
                                                <Box component="li" {...props} key={veh.vehid}>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {veh.vehmat}
                                                        </Typography>

                                                        <Typography variant="caption" color="text.secondary">
                                                            {veh.vehmar}{' '}
                                                            {veh.vehmod}
                                                            {veh.vehani ? ` — ${veh.vehani}` : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Buscar vehículo"
                                                    placeholder="Placa, marca o modelo"
                                                    margin="normal"
                                                    error={Boolean(errors.vehId)}
                                                    helperText={errors.vehId?.message}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <InputAdornment position="start">
                                                                    <DirectionsCarIcon
                                                                        color={errors.vehId ? 'error' : 'action'}
                                                                    />
                                                                </InputAdornment>
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                        endAdornment: (
                                                            <>
                                                                {loadingVehicles ? (
                                                                    <CircularProgress size={20} />
                                                                ) : null}

                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    );
                                }}
                            />
                        </Grid>

                        {/* Nombre del equipo */}
                        <Grid item xs={12}>
                            <TextField
                                label="Nombre del equipo (opcional)"
                                fullWidth
                                margin="normal"
                                disabled={submitting}
                                inputProps={{ maxLength: 150 }}
                                {...register('nombre')}
                                error={Boolean(errors.nombre)}
                                helperText={errors.nombre?.message}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <GroupIcon color={errors.nombre ? 'error' : 'action'} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* Servicios-Proyecto */}
                        <Grid item xs={12}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AssignmentIcon color="primary" />
                                Servicios-Proyecto habilitados
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Seleccione los servicios en los que este equipo puede operar.
                            </Typography>

                            {assignedServiceIds.length === 0 && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    El equipo todavía no tiene servicios-proyecto asignados.
                                </Alert>
                            )}

                            <Grid container spacing={2} mt={1}>
                                {/* Servicios disponibles */}
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AddCircleIcon color="primary" />
                                            Servicios disponibles
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Buscar servicio"
                                            value={serviceSearch}
                                            disabled={submitting}
                                            onChange={(e) => setServiceSearch(e.target.value)}
                                            sx={{ mb: 2 }}
                                        />
                                        {loadingServices ? (
                                            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                                                <CircularProgress size={28} />
                                            </Box>
                                        ) : filteredAvailableServices.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {serviceSearch.trim() ? 'No se encontraron servicios' : 'No hay servicios disponibles'}
                                            </Typography>
                                        ) : (
                                            <Stack spacing={1}>
                                                {filteredAvailableServices.map((service) => {
                                                    const id = getSerProId(service);
                                                    return (
                                                        <Paper key={id} variant="outlined" sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {getSerProNom(service)}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {getProNom(service)} — {getTipSerNom(service)}
                                                                </Typography>
                                                            </Box>
                                                            <IconButton
                                                                type="button"
                                                                color="primary"
                                                                size="small"
                                                                disabled={submitting}
                                                                onClick={() => handleAddService(service)}
                                                            >
                                                                <AddCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Paper>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Paper>
                                </Grid>

                                {/* Servicios asignados */}
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <RemoveCircleIcon color="primary" />
                                            Servicios asignados
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        {assignedServices.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No hay servicios asignados
                                            </Typography>
                                        ) : (
                                            <Stack spacing={1}>
                                                {assignedServices.map((service) => {
                                                    const id = getSerProId(service);
                                                    return (
                                                        <Paper key={id} variant="outlined" sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {getSerProNom(service)}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {getProNom(service)} — {getTipSerNom(service)}
                                                                </Typography>
                                                            </Box>
                                                            <IconButton
                                                                type="button"
                                                                color="error"
                                                                size="small"
                                                                disabled={submitting}
                                                                onClick={() => handleRemoveService(id)}
                                                            >
                                                                <RemoveCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Paper>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                        {errors.serProIds && (
                                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                                {errors.serProIds.message}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2, bgcolor: 'grey.100' }}>
                    <Button type="button" onClick={handleFormClose} variant="outlined" color="inherit" disabled={submitting}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {submitting ? 'Guardando...' : isEditing ? 'Actualizar' : 'Guardar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default TeamForm;